import styled from "styled-components";
import {
    Col,
    Divider,
    Drawer,
    Form,
    Image,
    Input,
    InputNumber,
    notification,
    Radio,
    Row,
    Select,
    Typography,
    Upload
} from "antd";
import React from "react";
import {useAppData} from "../../store/app-data";
import {MinusCircleOutlined, PlusOutlined} from "@ant-design/icons";
import {Button} from "../../components/actions/button";
import {useUploadFile} from "../../hooks/use-upload-file";
import {API_BASE_MEDIA_URL} from "../../config";
import {getInitialValues, whiteSpaceValidation} from "../../utils/helpers";
import {TranslatableInput, TranslatableTextarea} from "../../components/Translatable";
import {CloseSvg, RotateClockwiseSvg, SaveSvg} from "../../components/Svg";

const { Title } = Typography

const floatFields = [
    "price",
    "calories",
    "proteins",
    "fat",
    "carbohydrates",
    "size"
];

const DISCOUNT_VARS = [
    {
        value: "price",
        label: "цену"
    },
    {
        value: "discount",
        label: "скидку"
    },
]

export function EditDrawer(
    {
        title,
        state,
        initialValues,
        onClose,
        onSave,
        categoryOptions,
        productCategoryOptions,
        badgeOptions
    }
) {
    const layoutConfig = useAppData((state) => state.layoutConfig)
    const [previewImage, setPreviewImage] = React.useState(null);
    const [imagesFileList, setImagesFileList] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false)
    const [selectedCategories, setSelectedCategories] = React.useState([]);
    const [deletedPhotoIds, setDeletedPhotoIds] = React.useState([]);

    const [form] = Form.useForm();

    function getImage(image) {
        const file = {
            url: API_BASE_MEDIA_URL + image.path + "/" + image.systemName,
            uid: String(image.id),
            name: image.originalName,
            status: "done",
        }

        return [file]
    }

    function handleClose() {
        onClose()
        setImagesFileList([]);
        setSelectedCategories([])
        form.resetFields()
    }

    function handleRemovePhoto(index) {
        console.log("removed photo log")
    }

    async function onPreview(file) {
        if (file instanceof File) {
            file.url = await URL.createObjectURL(file)
        }
        setPreviewImage(file)
    }

    async function handleBeforeUpload(file, fieldPath, index = null) {
        try {
            const uploadedFile = await useUploadFile(file);
            if (uploadedFile?.id) {
                const newFile = {
                    uid: String(uploadedFile.id),
                    url: API_BASE_MEDIA_URL + uploadedFile.path + "/" + uploadedFile.systemName,
                    name: file.name,
                    status: "done",
                };

                if (imagesFileList[index]) {
                   setDeletedPhotoIds(prev => [
                       ...prev,
                       imagesFileList[index]?.[0]?.uid
                   ])
                }
                setImagesFileList((prev) => ({
                    ...prev,
                    [index]: [newFile],
                }));
                form.setFields([{ name: fieldPath, value: uploadedFile.id }]);
            }
            return false;
        } catch (error) {
            notification.error({message: "Ошибка загрузки изображения"})
        }
    }

    function handleRemovePhoto(index) {
        if (imagesFileList[index]?.[0]?.uid) {
            setDeletedPhotoIds((prev) => [...prev, imagesFileList[index][0].uid]);
        }

        setImagesFileList((prev) => {
            const newFiles = { ...prev };
            delete newFiles[index];
            return newFiles;
        });

        const photos = form.getFieldValue("photos") || [];
        photos.splice(index, 1);
        form.setFieldsValue({ photos });
    }

    function handleRemoveCategory(index) {
        setSelectedCategories((prev) => prev.filter((_, i) => i !== index));
    }
    function handleCategorySelect(index, categoryId) {
        const selectedCategory = categoryOptions.find((c) => c.id === categoryId) || null;

        setSelectedCategories((prev) => {
            const newCategories = [...prev];
            if (!newCategories[index]) {
                newCategories[index] = selectedCategory;
            } else {
                newCategories[index] = selectedCategory;
            }

            return newCategories;
        });
    }

    React.useEffect(() => {
        const photos = form.getFieldValue("photos") || [];
        if (photos.length === 0) {
            form.setFieldsValue({
                photos: [
                    {
                        image: undefined,
                        sortWeight: 0,
                        isActive: true,
                        isMain: true,
                    },
                ],
                virtualCategories: [
                    {
                        virtualCategoryId: undefined,
                    }
                ]
            });
        }
    }, [state]);

    React.useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...getInitialValues(initialValues),
                discountOrPrice:
                    initialValues.price === null
                        ? "discount"
                        : "price",
            });

            let photosFileList = [];

            if (initialValues.photos) {
                initialValues.photos.forEach((photo, index) => {
                    photosFileList[index] = getImage(photo.file);
                });
            }
            setImagesFileList(photosFileList);

            let virtualCategoriesList = [];
            if (initialValues.virtualCategories?.length > 0) {
                initialValues.virtualCategories.forEach((vCategory, index) => {
                    virtualCategoriesList[index] = categoryOptions.find((c) => c.id === vCategory.virtualCategoryId) || null;
                });
            }

            setSelectedCategories(virtualCategoriesList)
        }
    }, [initialValues]);

    async function onReset() {
        if (initialValues) {
            await form.resetFields()
            await form.setFieldsValue(initialValues)
            return
        }
        form.resetFields()
    }

    const id = initialValues?.id ?? "new"

    async function onSubmit(values) {
        setIsLoading(true)
        const id = initialValues?.id

        const data = {
            ...(id && { id, _method: "PUT" }),
            isActive: values.isActive ? 1 : 0,
            isShowToMain: values.isShowToMain ? 1 : 0,
            ...(values.discountOrPrice === "price" && {price: Number(values.price).toFixed(2), discountPercentage: null}),
            ...(values.discountOrPrice === "discount" && {price: null, discountPercentage: values.discountPercentage}),
            discountPercentage: values.discountPercentage,
            calories: values.calories ? Number(values.calories).toFixed(2) : "0.00",
            proteins: values.proteins ? Number(values.proteins).toFixed(2) : "0.00",
            fat: values.fat ? Number(values.fat).toFixed(2) : "0.00",
            carbohydrates: values.carbohydrates ? Number(values.carbohydrates).toFixed(2) : "0.00",
            size: values.size ? Number(values.size).toFixed(2) : "0.00",
            vat: values.vat,
            sortWeight: values.sortWeight,
        };

        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => formData.append(key, value))

        values.dashboardProductCategoryIds.forEach((productCategoryId, index) => {
            formData.append(`dashboardProductCategoryIds[${index}]`, productCategoryId)
        })
        if (values.dashboardProductLabelIds?.length > 0) {
            values.dashboardProductLabelIds.forEach((productLabelId, index) => {
                formData.append(`dashboardProductLabelIds[${index}]`, productLabelId)
            })
        } else {
            formData.append('dashboardProductLabelIds', '[]');
        }

        let indexName = 0
        Object.entries(values.name).forEach(([key, value]) => {
            if (value !== undefined) {
                formData.append(`name[${indexName}][language]`, key)
                formData.append(`name[${indexName}][content]`, value)
                indexName++
            }
        })
        let indexShortName = 0
        Object.entries(values.shortName).forEach(([key, value]) => {
            if (value !== undefined) {
                formData.append(`shortName[${indexShortName}][language]`, key)
                formData.append(`shortName[${indexShortName}][content]`, value)
                indexShortName++
            }
        })
        let indexDescription = 0
        Object.entries(values.description).forEach(([key, value]) => {
            if (value !== undefined) {
                formData.append(`description[${indexDescription}][language]`, key)
                formData.append(`description[${indexDescription}][content]`, value)
                indexDescription++
            }
        })

        deletedPhotoIds.forEach((photoId, index) => {
            formData.append(`deletedPhotoIds[${index}]`, photoId);
        });

        values.photos.forEach((photo, index) => {
            formData.append(`photos[${index}][fileId]`, imagesFileList[index][0].uid)
            formData.append(`photos[${index}][sortWeight]`, photo.sortWeight)
            formData.append(`photos[${index}][isActive]`, photo.isActive ? 1 : 0)
            formData.append(`photos[${index}][isMain]`, photo.isMain ? 1 : 0)
        })

        values.virtualCategories.forEach((vCategory, index) => {
            Object.entries(vCategory).forEach(([key, value]) => {
                formData.append(`virtualCategories[${index}][${key}]`, value)
            })
        })

        try {
            await onSave(formData, initialValues?.id)

            form.resetFields()
            handleClose()
        } catch (error) {
            console.error("Ошибка отпраки формы")
        } finally {
            setIsLoading(false)
        }
    }

    const discountOrPrise = Form.useWatch("discountOrPrice", form)

    return (
        <DrawerEdit
            width={layoutConfig.isMobile ? "90%" : "50%"}
            title={title}
            onClose={handleClose}
            open={state}
        >
            <StyledImage
                alt={previewImage?.name || "Превью"}
                preview={{
                    visible: previewImage,
                    onVisibleChange: () => setPreviewImage(null),
                }}
                src={previewImage?.url || previewImage?.thumbUrl}
            />
            <FormEdit
                form={form}
                layout={"vertical"}
                onFinish={(values) => onSubmit(values)}
            >
                <Divider orientation="left">Обложки</Divider>
                <Form.List name="photos">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({key, name, ...field}, index) => (
                                <React.Fragment key={`PhotosRowTitle_${index}`}>
                                    <RowTitle level={4}>
                                        Обложка {key + 1}{" "}
                                        {(fields.length > 1 && index !== 0) ? (
                                            <MinusCircleOutlined
                                                className="dynamic-delete-button"
                                                style={{ marginLeft: "12px" }}
                                                onClick={() => {
                                                    handleRemovePhoto(index)
                                                    remove(name);
                                                }}
                                            />
                                        ) : null}
                                    </RowTitle>
                                    <Form.Item required={false} key={key}>
                                        <RowFlex>
                                            <StyledFormItem
                                                label={"Изображение"}
                                                name={[name, "image"]}
                                                rules={[{ required: imagesFileList[index] ? false : true, message: "Выберите изображение!" }]}
                                            >
                                                <Upload
                                                    listType="picture-card"
                                                    maxCount={1}
                                                    accept="image/jpeg,image/png,image/jpg"
                                                    onPreview={onPreview}
                                                    beforeUpload={(file) => handleBeforeUpload(file, ["photos", name, "image"], name)}
                                                    fileList={Array.isArray(imagesFileList[name]) ? imagesFileList[name] : []}
                                                >
                                                    <div>
                                                        <PlusOutlined />
                                                        <div style={{ marginTop: 8 }}>Загрузить изображение</div>
                                                    </div>
                                                </Upload>
                                            </StyledFormItem>
                                            <RowFlexColumn>
                                                <StyledFormItem
                                                    {...field}
                                                    key={`${id}_${key}_sortWeight`}
                                                    label={"Позиция в списке"}
                                                    name={[name, "sortWeight"]}
                                                    rules={[
                                                        {
                                                            message: "Введите позицию!",
                                                            required: true,
                                                        },
                                                    ]}
                                                >
                                                    <InputNumber min={0} style={{ width: "100%" }}/>
                                                </StyledFormItem>
                                                <Row gutter={32} style={{ flex: 1, gap: "10px" }}>
                                                    <Col className="gutter-row" span={10}>
                                                        <StyledFormItem
                                                            {...field}
                                                            key={id + "_isActive"}
                                                            label={"Активное"}
                                                            name={[name, "isActive"]}
                                                            initialValue={form?.photos?.[index]?.isActive === undefined
                                                                ? true
                                                                : form?.photos?.[index]?.isActive
                                                            }
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: "Please select!",
                                                                },
                                                            ]}
                                                        >
                                                            <Radio.Group
                                                                buttonStyle="solid"
                                                                value={form?.photos?.[index]?.isActive === undefined
                                                                    ? true
                                                                    : form?.photos?.[index]?.isActive
                                                                }
                                                            >
                                                                <Radio.Button value={true}>Да</Radio.Button>
                                                                <Radio.Button value={false}>Нет</Radio.Button>
                                                            </Radio.Group>
                                                        </StyledFormItem>
                                                    </Col>
                                                    <Col className="gutter-row" span={10}>
                                                        <StyledFormItem
                                                            {...field}
                                                            key={id + "_isMain"}
                                                            label={"Главное"}
                                                            name={[name, "isMain"]}
                                                            initialValue={form?.photos?.[index]?.isMain === undefined
                                                                ? false
                                                                : form?.photos?.[index]?.isMain
                                                            }
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: "Please select!",
                                                                },
                                                            ]}
                                                        >
                                                            <Radio.Group
                                                                buttonStyle="solid"
                                                                value={form?.photos?.[index]?.isMain === undefined
                                                                    ? false
                                                                    : form?.photos?.[index]?.isMain
                                                                }
                                                                onChange={() => handleIsMainChange(index)}
                                                            >
                                                                <Radio.Button value={true}>Да</Radio.Button>
                                                                <Radio.Button value={false}>Нет</Radio.Button>
                                                            </Radio.Group>
                                                        </StyledFormItem>
                                                    </Col>
                                                </Row>
                                            </RowFlexColumn>
                                        </RowFlex>
                                    </Form.Item>
                                </React.Fragment>
                            ))}

                            <Row gutter={28}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item>
                                        <Button
                                            type="dashed"
                                            onClick={() => add()}
                                            style={{ width: "100%" }}
                                            icon={<PlusOutlined />}
                                        >
                                            Добавить
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form.List>

                <Divider orientation="left">Виртуальные категории</Divider>
                <Form.List name="virtualCategories">
                    {(fields, { add, remove }) => (
                            <>
                                {fields.map(({key, name, ...field}, index) => (
                                    <React.Fragment key={`CategoryRowTitle_${index}`}>
                                        <RowTitle level={4}>
                                            Категория {key + 1}{" "}
                                            {(fields.length > 1 && index !== 0) ? (
                                                <MinusCircleOutlined
                                                    className="dynamic-delete-button"
                                                    style={{ marginLeft: "12px" }}
                                                    onClick={() => {
                                                        handleRemovePhoto(index, imagesFileList[index]);
                                                        remove(name)
                                                    }}
                                                />
                                            ) : null}
                                        </RowTitle>
                                        <Form.Item required={false} key={key}>
                                            <StyledFormItem
                                                key={id + "_" + name + "_virtualCategoryId"}
                                                label={"Виртуальная категория"}
                                                name={[name, "virtualCategoryId"]}
                                                rules={[
                                                    { required: true, message: "Выберите категорию!" },
                                                ]}
                                            >
                                                <Select
                                                    placeholder={"Выберите тип"}
                                                    options={categoryOptions.map(category => ({
                                                        value: category.id,
                                                        label: category.name.find(n => n.language === "ru")?.content ?? "Название отсутствует",
                                                    }))}
                                                    onChange={(value) => handleCategorySelect(index, value)}
                                                />
                                            </StyledFormItem>
                                            {selectedCategories[index] &&
                                                <StyledFormItem
                                                    {...field}
                                                    key={id + "_mainProductId"}
                                                    label={"Главный продукт"}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Выберите продукт!",
                                                        },
                                                    ]}
                                                    name={[name, "mainProductId"]}
                                                >
                                                    <Radio.Group>
                                                        {selectedCategories[name].products.map((product, idx) => {
                                                            const name = product.short_name.find((sn) => sn.language === "ru")?.content ?? "Название отсутствует"

                                                            return (
                                                                <Radio key={`product_ratio_${index}_${idx}`} value={product.id}>{name}</Radio>
                                                            )
                                                        })}
                                                    </Radio.Group>
                                                </StyledFormItem>
                                            }
                                        </Form.Item>
                                    </React.Fragment>
                                ))}

                                <Row gutter={28}>
                                    <Col className="gutter-row" span={12}>
                                        <Form.Item>
                                            <Button
                                                type="dashed"
                                                onClick={() => add()}
                                                style={{ width: "100%" }}
                                                icon={<PlusOutlined />}
                                            >
                                                Добавить
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>
                    )}
                </Form.List>
                <Divider orientation={"left"}>Отображение</Divider>
                <StyledFormItem
                    key={id + "_dashboardProductCategoryIds"}
                    label={"Категории продуктов для отображения"}
                    name={"dashboardProductCategoryIds"}
                    rules={[
                        {
                            required: true,
                            message: "Выберите категорию!",
                        },
                    ]}
                >
                    <Select
                        mode={"multiple"}
                        options={productCategoryOptions}
                        optionFilterProp="label"
                        placeholder={"Выберите категорию"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    label={"Лейблы"}
                    name={"dashboardProductLabelIds"}
                    key={id + "_dashboardProductLabelIds"}
                >
                    <Select
                        optionFilterProp="label"
                        mode={"multiple"}
                        options={badgeOptions}
                        placeholder={"Выберите лейбл"}
                    />
                </StyledFormItem>
                <Divider orientation={"left"}>Описание набора</Divider>
                <StyledFormItem label={"Полное название"}>
                    <TranslatableInput
                        name={"name"}
                        id={id}
                    />
                </StyledFormItem>
                <StyledFormItem label={"Короткое название"}>
                    <TranslatableInput
                        name={"shortName"}
                        id={id}
                    />
                </StyledFormItem>
                <StyledFormItem label={"Описание"}>
                    <TranslatableTextarea
                        name={"description"}
                        id={id}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_isActive"}
                    label={"Активный"}
                    name={"isActive"}
                    initialValue={form.isActive ? form.isActive : true}
                    rules={[
                        {
                            required: true,
                            message: "Please select!",
                        },
                    ]}
                >
                    <Radio.Group buttonStyle="solid" value={form.isActive}>
                        <Radio.Button value={true}>Да</Radio.Button>
                        <Radio.Button value={false}>Нет</Radio.Button>
                    </Radio.Group>
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_isShowToMain"}
                    label={"Показывать на главной"}
                    name={"isShowToMain"}
                    initialValue={form.isShowToMain ? form.isShowToMain : true}
                    rules={[
                        {
                            required: true,
                            message: "Please select!",
                        },
                    ]}
                >
                    <Radio.Group buttonStyle="solid" value={form.isShowToMain}>
                        <Radio.Button value={true}>Да</Radio.Button>
                        <Radio.Button value={false}>Нет</Radio.Button>
                    </Radio.Group>
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_discountOrPrice"}
                    label={"Указывать цену или скидку"}
                    name={"discountOrPrice"}
                    rules={[
                        {
                            required: true,
                            message: "Пожалуйста выберите!",
                        },
                    ]}
                >
                    <Radio.Group buttonStyle="solid">
                        {DISCOUNT_VARS.map((disItem) => (
                            <Radio.Button value={disItem.value} key={`discount_${disItem.value}`}>{disItem.label}</Radio.Button>
                        ))}
                    </Radio.Group>
                </StyledFormItem>
                {discountOrPrise === "price" && (
                    <StyledFormItem
                        key={id + "_price"}
                        label={"Цена"}
                        name={"price"}
                        rules={[
                            {
                                required: true,
                                message: "Введите цену"
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            style={{
                                width: "100%",
                            }}
                            placeholder={"Цена"}
                        />
                    </StyledFormItem>
                )}
                {discountOrPrise === "discount" && (
                    <StyledFormItem
                        key={id + "_discountPercentage"}
                        label={"Размер скидки(%)"}
                        name={"discountPercentage"}
                        rules={[
                            {
                                required: true,
                                message: "Введите размер скидки!",
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            style={{
                                width: "100%",
                            }}
                            placeholder={"Размер скидки(%)"}
                        />
                    </StyledFormItem>
                )}

                <StyledFormItem
                    key={id + "_calories"}
                    label={"Калории"}
                    name={"calories"}
                    rules={[
                        {
                            required: true,
                            message: "Введите калории!",
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Калории"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_proteins"}
                    label={"Белки"}
                    name={"proteins"}
                    rules={[
                        {
                            required: true,
                            message: "Введите количество белков!",
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Белки"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_fat"}
                    label={"Жиры"}
                    name={"fat"}
                    rules={[
                        {
                            required: true,
                            message: "Введите количество жиров!",
                        },
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Жиры"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_carbohydrates"}
                    label={"Углеводы"}
                    name={"carbohydrates"}
                    rules={[
                        {
                            required: true,
                            message: "Введите количество углеводов!",
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Углеводы"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_size"}
                    label={"Размер"}
                    name={"size"}
                    rules={[
                        {
                            required: true,
                            message: "Введите размер!",
                        },
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Размер"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_vat"}
                    label={"НДС"}
                    name={"vat"}
                    rules={[
                        {
                            required: true,
                            message: "Введите НДС!",
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"НДС"}
                    />
                </StyledFormItem>
                <StyledFormItem
                    key={id + "_sortWeight"}
                    label={"Позиция в списке"}
                    name={"sortWeight"}
                    rules={[
                        {
                            required: true,
                            message: "Введите номер позиции!",
                        }
                    ]}
                >
                    <InputNumber
                        min={0}
                        style={{
                            width: "100%",
                        }}
                        placeholder={"Позиция в списке"}
                    />
                </StyledFormItem>


                <ButtonRow>
                    <ButtonCol>
                        <Button danger onClick={onReset} icon={<RotateClockwiseSvg />}>
                            Вернуть
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button onClick={handleClose} icon={<CloseSvg />}>
                            Закрыть
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button
                            icon={<SaveSvg />}
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            Сохранить
                        </Button>
                    </ButtonCol>
                </ButtonRow>
            </FormEdit>
        </DrawerEdit>

    )
}

const DrawerEdit = styled(Drawer)``

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`

const StyledImage = styled(Image)`
  display: none;
`

const RowFlex = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 8px;
`

const RowFlexColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const RowTitle = styled(Title)`
  margin-bottom: 20px;
`
